import type { SupabaseClient } from "@supabase/supabase-js";

// Commercial/admin readiness reads the Task 59a/59b foundations without adding a new backend
// surface. Keep selects pinned: no ids, relationship account ids, metadata, Stripe refs, or
// storage keys are rendered by this module.

export type BillingMode = "self" | "external" | "unknown";
export type CommercialEntitlementStatus = "active" | "trialing" | "disabled" | "expired";

export interface CommercialContext {
  billingMode: BillingMode;
  operatorAccountCount: number;
  brandDisplayName: string | null;
  brandKey: string | null;
  themeKey: string | null;
}

export interface CommercialEntitlementRow {
  featureKey: string;
  status: CommercialEntitlementStatus;
  limitValue: number | null;
  softLimitValue: number | null;
  startsAt: string | null;
  expiresAt: string | null;
}

export interface CommercialUsageCounterRow {
  featureKey: string;
  periodStart: string | null;
  periodEnd: string | null;
  usageCount: number;
}

export interface CommercialReadinessData {
  context: CommercialContext;
  entitlements: CommercialEntitlementRow[];
  usageCounters: CommercialUsageCounterRow[];
}

export interface CommercialReadinessSummary {
  entitlementCount: number;
  enabledEntitlementCount: number;
  usageCounterCount: number;
  hasBrandProfile: boolean;
  hasExternalBilling: boolean;
  hasOperatorRelationships: boolean;
}

const ENTITLEMENT_COLUMNS = "feature_key, status, limit_value, soft_limit_value, starts_at, expires_at";
const USAGE_COUNTER_COLUMNS = "feature_key, period_start, period_end, usage_count";
const ENTITLEMENT_LIMIT = 50;
const USAGE_COUNTER_LIMIT = 50;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function asCount(value: unknown): number {
  return asNumber(value) ?? 0;
}

function asEntitlementStatus(value: unknown): CommercialEntitlementStatus {
  if (value === "active" || value === "trialing" || value === "disabled" || value === "expired") {
    return value;
  }
  return "disabled";
}

export function mapCommercialContext(raw: Record<string, unknown>, accountId: string): CommercialContext {
  const billingAccountId = asString(raw.billing_account_id);
  const operatorIds = Array.isArray(raw.operator_account_ids) ? raw.operator_account_ids : [];
  const brandDisplayName = asString(raw.brand_display_name);
  const brandKey = asString(raw.brand_key);
  const themeKey = asString(raw.theme_key);
  return {
    billingMode: billingAccountId === null ? "unknown" : billingAccountId === accountId ? "self" : "external",
    operatorAccountCount: operatorIds.filter((value) => typeof value === "string" && value.length > 0).length,
    brandDisplayName,
    brandKey,
    themeKey,
  };
}

export function mapCommercialEntitlementRow(raw: Record<string, unknown>): CommercialEntitlementRow | null {
  const featureKey = asString(raw.feature_key);
  if (featureKey === null) {
    return null;
  }
  return {
    featureKey,
    status: asEntitlementStatus(raw.status),
    limitValue: asNumber(raw.limit_value),
    softLimitValue: asNumber(raw.soft_limit_value),
    startsAt: asString(raw.starts_at),
    expiresAt: asString(raw.expires_at),
  };
}

export function mapCommercialUsageCounterRow(raw: Record<string, unknown>): CommercialUsageCounterRow | null {
  const featureKey = asString(raw.feature_key);
  if (featureKey === null) {
    return null;
  }
  return {
    featureKey,
    periodStart: asString(raw.period_start),
    periodEnd: asString(raw.period_end),
    usageCount: asCount(raw.usage_count),
  };
}

export function summarizeCommercialReadiness(data: CommercialReadinessData): CommercialReadinessSummary {
  return {
    entitlementCount: data.entitlements.length,
    enabledEntitlementCount: data.entitlements.filter((row) => row.status === "active" || row.status === "trialing").length,
    usageCounterCount: data.usageCounters.length,
    hasBrandProfile: data.context.brandDisplayName !== null || data.context.brandKey !== null,
    hasExternalBilling: data.context.billingMode === "external",
    hasOperatorRelationships: data.context.operatorAccountCount > 0,
  };
}

async function fetchCommercialContext(client: SupabaseClient, accountId: string): Promise<CommercialContext> {
  const { data, error } = await client.rpc("account_commercial_context", { p_account_id: accountId });
  if (error !== null) {
    throw new Error("Could not load commercial readiness.");
  }
  const row = Array.isArray(data) && typeof data[0] === "object" && data[0] !== null ? data[0] : {};
  return mapCommercialContext(row as Record<string, unknown>, accountId);
}

async function fetchCommercialEntitlements(
  client: SupabaseClient,
  accountId: string,
): Promise<CommercialEntitlementRow[]> {
  const { data, error } = await client
    .from("account_feature_entitlements")
    .select(ENTITLEMENT_COLUMNS)
    .eq("account_id", accountId)
    .order("feature_key", { ascending: true })
    .limit(ENTITLEMENT_LIMIT);
  if (error !== null) {
    throw new Error("Could not load commercial readiness.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapCommercialEntitlementRow(row as Record<string, unknown>))
    .filter((row): row is CommercialEntitlementRow => row !== null);
}

async function fetchCommercialUsageCounters(
  client: SupabaseClient,
  accountId: string,
): Promise<CommercialUsageCounterRow[]> {
  const { data, error } = await client
    .from("account_usage_counters")
    .select(USAGE_COUNTER_COLUMNS)
    .eq("account_id", accountId)
    .order("period_end", { ascending: false })
    .limit(USAGE_COUNTER_LIMIT);
  if (error !== null) {
    throw new Error("Could not load commercial readiness.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapCommercialUsageCounterRow(row as Record<string, unknown>))
    .filter((row): row is CommercialUsageCounterRow => row !== null);
}

/** Load commercial context + pinned entitlement/usage columns for the selected account. */
export async function fetchCommercialReadiness(
  client: SupabaseClient,
  accountId: string,
): Promise<CommercialReadinessData> {
  const [context, entitlements, usageCounters] = await Promise.all([
    fetchCommercialContext(client, accountId),
    fetchCommercialEntitlements(client, accountId),
    fetchCommercialUsageCounters(client, accountId),
  ]);
  return { context, entitlements, usageCounters };
}
